import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Space, Tooltip } from "antd";
import { Button, Col, Dropdown, Form, Modal, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatDate,
  isoDateFormat,
  isValidDecimalInput,
} from "../../utils/Utils";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import {
  getPaymentHistory,
  updatePaymentDetails,
  clearUpdateMessage,
  setPagination,
  pauseClientSubscription,
  clearPauseMessage,
  endClientSubscription,
  clearEndMessage,
} from "../../redux/Slices/clientPaymentTabSlice";
import FormLabel from "../../components/Common/FormLabel";
import Loader from "../../components/Common/Loader";
import FormControl from "../../components/Common/FormControl";
import { Table, Typography } from "antd";
import ToastNotification from "../../components/Common/ToastNotification";

const { Text } = Typography;

const ClientPaymentDetailsTab = ({ selectedClientId, coachId }) => {
  const { trans } = useCustomTranslation();

  const dispatch = useDispatch();
  const {
    status,
    paymentMonthStatus,
    paymentOverview,
    subscriptionEndDate,
    loading,
  } = useSelector((state) => state.membershipOverview);

  const {
    paymentData,
    totalCount,
    currentPage,
    pageSize,
    paymentDataLoading,
    updateSuccessMessage,
    updateFailedMessage,
    pauseSuccessMessage,
    pauseFailedMessage,
    endSuccessMessage,
    endFailedMessage,
  } = useSelector((state) => state.payment);

  const [filteredData, setFilteredData] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectClientStatus, setSelectedClientStatus] = useState(
    status || "Active"
  );
  const [isPauseSubscription, setIsPauseSubscription] = useState(false);
  const [isEndSubscription, setIsEndSubscription] = useState(false);
  const [isSubscriptionConfirmation, setIsSubscriptionConfirmation] =
    useState(false);
  const [
    subscriptionConfirmationMessage,
    setIsSubscriptionConfirmationMessage,
  ] = useState("");
  const [selectedOption, setSelectedOption] = useState(
    "endSubscriptionAfterCurrentPaymentPeriod"
  );

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [currencyValue, setCurrencyValue] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [updateFromDate, setUpdateFromDate] = useState(null);
  const [resumeSubscriptionDate, setResumeSubscriptionDate] = useState(null);
  const [isEditFromTable, setIsEditFromTable] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);
  const [errors, setErrors] = useState({
    currencyValue: "",
    dueDate: "",
    resumeSubscriptionDate: "",
  });
  const [pauseSubscriptionPayload, setPauseSubscriptionPayload] = useState({});
  const [endSubscriptionPayload, setEndSubscriptionPayload] = useState({});
  const [nextDueDate, setNextDueDate] = useState(null);

  const clientId = selectedClientId;

  const billingCycles = [
    { value: "monthApart", label: trans("LeadPage.monthApart") },
    { value: "startOfTheMonth", label: trans("LeadPage.startOfTheMonth") },
    {
      value: "lastWeekDayOfMonth",
      label: trans("LeadPage.lastWeekDayOfMonth"),
    },
    { value: "custom", label: trans("LeadPage.custom") },
  ];

  useEffect(() => {
    if (clientId) {
      dispatch(
        getPaymentHistory({
          clientId,
          pageNumber: currentPage,
          pageSize,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Sync filteredData with paymentData
  useEffect(() => {
    setFilteredData(paymentData);
  }, [paymentData]);

  useEffect(() => {
    if (filteredData && filteredData.length > 0) {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      const sortedPayments = filteredData
        .filter((payment) => payment.payStatus === "Pending")
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

      const nextDue = sortedPayments.find(
        (payment) => new Date(payment.dueDate) >= today
      );
      const nextMonthDue = sortedPayments.find(
        (payment) => new Date(payment.dueDate) >= nextMonth
      );

      if (nextDue) {
        setNextDueDate(nextDue.dueDate);
      } else if (nextMonthDue) {
        setNextDueDate(nextMonthDue.dueDate);
      }
    }
  }, [filteredData]);

  // Payment bar color logic
  const getPaymentBarColor = (status) => {
    switch (status) {
      case "Received":
      case "Success":
        return "#28A745";
      case "Failed":
      case "Cancelled":
        return "#DC3545";
      case "Pending":
      case "Processing":
      case "Next":
        return "#A0A0A0";
      default:
        return "#6C757D";
    }
  };

  // Handle currency value input changes
  const onCurrencyValueChange = (e) => {
    const { value } = e.target;
    if (value === "0") {
      setErrors({
        ...errors,
        currencyValue: trans("ClientPaymentTab.validAmount"),
      });
    } else if (isValidDecimalInput(value)) {
      setCurrencyValue(value);
      setErrors({ ...errors, currencyValue: "" });
    }

    if (!value) {
      setErrors({
        ...errors,
        currencyValue: trans("ClientPaymentTab.validAmount"),
      });
    }
  };

  // Validate form inputs
  const validateForm = () => {
    if (isEndSubscription) {
      return true;
    }
    let isValid = true;
    const newErrors = {
      currencyValue: "",
      dueDate: "",
      resumeSubscriptionDate: "",
    };

    if (
      (!currencyValue || currencyValue === "0") &&
      !isPauseSubscription &&
      !isEndSubscription
    ) {
      newErrors.currencyValue = trans("ClientPaymentTab.validAmount");
      isValid = false;
    }

    if (isEditFromTable && !dueDate) {
      newErrors.dueDate = trans("ClientPaymentTab.validDueDate");
      isValid = false;
    }
    if (!isEditFromTable && !updateFromDate && !isPauseSubscription) {
      newErrors.updateFromDate = trans("ClientPaymentTab.validFromDate");
      isValid = false;
    }

    if (isPauseSubscription && !resumeSubscriptionDate) {
      newErrors.resumeSubscriptionDate = trans(
        "ClientPaymentTab.validResumeDate"
      );
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle modal confirmation
  const handleConfirm = () => {
    if (!validateForm()) {
      // Stop if validation fails
      return;
    }

    if (isPauseSubscription) {
      // Handle Pause Subscription logic
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      setIsSubscriptionConfirmationMessage(
        `${trans(
          "ClientPaymentTab.resumeSubscriptionConfirmationMessage"
        )} ${formatDate(resumeSubscriptionDate)}`
      );
      const payload = {
        clientId: clientId,
        resumeDate: resumeSubscriptionDate
          ? isoDateFormat(resumeSubscriptionDate)
          : null,
        coachId,
        pageNumber: currentPage,
        pageSize,
      };
      setPauseSubscriptionPayload(payload);
    } else if (isEndSubscription) {
      // Handle End Subscription logic
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      selectedOption === "endSubscriptionToday"
        ? setIsSubscriptionConfirmationMessage(
            trans("ClientPaymentTab.endSubscriptionConfirmationMessage")
          )
        : setIsSubscriptionConfirmationMessage(
            `Are you sure you want to end this subscription after the current payment period? The subscription will remain active until ${
              nextDueDate ? formatDate(nextDueDate) : ""
            }`
          );
      const payload = {
        clientId,
        endImmediately: selectedOption === "endSubscriptionToday",
        endAfterBilling:
          selectedOption === "endSubscriptionAfterCurrentPaymentPeriod",
        coachId,
        pageNumber: currentPage,
        pageSize,
      };
      setEndSubscriptionPayload(payload);
    } else if (isEditFromTable) {
      if (selectedPayment) {
        const paymentDetails = {
          paymentId: selectedPayment?.paymentId,
          isMonthlyPayment: true,
          amount: currencyValue,
          dueDate: dueDate ? isoDateFormat(dueDate) : null,
          updateFromDate: null,
        };

        setSelectedPayment(null);
        setCurrencyValue("");
        setDueDate(null);
        dispatch(
          updatePaymentDetails({
            clientId,
            paymentDetails,
            pageNumber: currentPage,
            pageSize,
          })
        );
        setIsDialogOpen(false);
      }
    } else {
      const paymentDetails = {
        paymentId: null,
        isMonthlyPayment: false,
        amount: currencyValue,
        dueDate: null,
        updateFromDate: isoDateFormat(updateFromDate),
      };

      // const clientId = "45AF361A-721B-4730-847E-6B838585C3F6";
      dispatch(
        updatePaymentDetails({
          clientId,
          paymentDetails,
          pageNumber: currentPage,
          pageSize,
        })
      );
      // Close the modal after updating the data
      setIsDialogOpen(false);
    }
  };

  const handleSubscriptionConfirm = () => {
    if (isPauseSubscription) {
      dispatch(pauseClientSubscription(pauseSubscriptionPayload));
      setResumeSubscriptionDate(null);
      setPauseSubscriptionPayload({});
      setIsPauseSubscription(false);
    } else if (isEndSubscription) {
      dispatch(endClientSubscription(endSubscriptionPayload));
      setEndSubscriptionPayload({});
      setIsEndSubscription(false);
    }
    setIsSubscriptionConfirmation(false);
  };
  // Open the dialog for editing a payment record from the table row
  const openDialogFromTable = (payment) => {
    setSelectedPayment(payment);
    setCurrencyValue(payment.amount);
    setDueDate(payment.dueDate ? new Date(payment.dueDate) : null);
    setIsEditFromTable(true);
    setIsDialogOpen(true);
    setIsPauseSubscription(false);
    setIsEndSubscription(false);
  };

  // Open the dialog for editing a recurring payment (not from table)
  const openDialogFromPencil = () => {
    setSelectedPayment(null);
    setUpdateFromDate(null);
    setCurrencyValue(
      paymentOverview?.monthlyAmount ? paymentOverview?.monthlyAmount : 0
    );
    setDueDate(null);
    setIsEditFromTable(false);
    setIsPauseSubscription(false);
    setIsEndSubscription(false);
    setIsDialogOpen(true);
  };

  // Close the dialog
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  // Define columns for the table
  const columns = [
    {
      title: trans("Common.amount"),
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => `${record?.currency} ${text}`, // Formatting the amount as currency
    },
    {
      title: trans("Common.type"),
      dataIndex: "paymentType",
      key: "paymentType",
      render: (paymentType) => {
        let backgroundClass = "";
        let text = paymentType;

        switch (paymentType) {
          case "paid":
            backgroundClass = "bg-teal-100";
            text = "Paid";
            break;
          case "Monthly":
            backgroundClass = "bg-inactive";
            text = "Monthly Payment";
            break;
          case "NotSetup":
            backgroundClass = "bg-invalid";
            text = "Not Setup";
            break;
          default:
            backgroundClass = "bg-invalid";
            text = paymentType;
            break;
        }

        return (
          <span
            className={`status-badge ${backgroundClass} d-flex justify-content-center`}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: trans("ClientPaymentTab.dueDate"),
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate) => <span>{dueDate ? formatDate(dueDate) : "-"}</span>,
    },
    {
      title: trans("ClientPaymentTab.paidDate"),
      dataIndex: "paidDate",
      key: "paidDate",
      render: (paidDate) => (
        <span>{paidDate ? formatDate(paidDate) : "-"}</span>
      ),
    },
    {
      title: trans("Common.status"),
      dataIndex: "payStatus",
      key: "payStatus",
      render: (text) => `${text}`,
    },
    {
      title: trans("Common.action"),
      key: "action",
      render: (text, record) => {
        const isFutureDate = new Date(record.dueDate) > new Date();

        return (
          <Space size="small">
            {isFutureDate && (
              <Tooltip title={"Edit"}>
                <Button
                  type="button"
                  disabled={
                    selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive"
                  }
                  onClick={() => openDialogFromTable(record)}
                  className="action-buttons pl-0"
                >
                  <i className="bi bi-pencil"></i>
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // const totalCount = filteredData.length;

  const handlePageChange = (pagination) => {
    const { current, pageSize } = pagination;

    dispatch(setPagination({ page: current, pageSize }));

    dispatch(
      getPaymentHistory({
        clientId,
        pageNumber: current,
        pageSize,
      })
    );
  };

  // Showing text for pagination
  const getShowingText = () => {
    if (filteredData.length <= 1) return "";
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalCount);
    return `${trans("Common.showing")} ${start} ${trans(
      "Common.to"
    )} ${end} ${trans("Common.of")} ${totalCount} ${trans("Common.results")}`;
  };

  const subscriptionMenuItems = [
    { key: "pause", label: "Pause Subscription" },
    { key: "end", label: "End Subscription" },
  ];

  const handleSubscriptionChange = (itemKey) => {
    if (itemKey === "pause") {
      setIsPauseSubscription(true);
      setIsEndSubscription(false);
    } else if (itemKey === "end") {
      setIsPauseSubscription(false);
      setIsEndSubscription(true);
    }
    setIsDialogOpen(true);
  };

  // Toast notification logic
  useEffect(() => {
    if (updateSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: updateSuccessMessage,
        heading: trans("Common.success"),
      });
      dispatch(clearUpdateMessage());
    } else if (updateFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: updateFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearUpdateMessage());
    } else if (pauseSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: pauseSuccessMessage,
        heading: trans("Common.success"),
      });
      setSelectedClientStatus("Paused");
      dispatch(clearPauseMessage());
    } else if (pauseFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: pauseFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearPauseMessage());
    } else if (endSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: endSuccessMessage,
        heading: trans("Common.success"),
      });
      setSelectedClientStatus("Paused");
      dispatch(clearEndMessage());
    } else if (endFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: endFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearEndMessage());
    }
  }, [
    dispatch,
    updateSuccessMessage,
    updateFailedMessage,
    pauseSuccessMessage,
    pauseFailedMessage,
    endSuccessMessage,
    endFailedMessage,
    trans,
  ]);

  return (
    <div className="clientDetailsInfoTabContainer-payment">
      <Row className="align-items-center mb-3">
        <Col>
          <h6>{trans("Common.payment") + " " + trans("Common.overview")}</h6>
        </Col>
        <Col className="text-end align-items-center">
          {selectClientStatus === "Active" && (
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="saveButton d-flex align-items-center pt-1 pb-1 fs-6 rounded-5"
              >
                Subscription
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {subscriptionMenuItems.map((item) => (
                  <Tooltip
                    key={`tooltip-${item.key}`}
                    placement="right"
                    title={
                      item.key === "pause"
                        ? trans("ClientPaymentTab.pauseSubscriptionTooltip")
                        : trans("ClientPaymentTab.endSubscriptionTooltip")
                    }
                  >
                    <Dropdown.Item
                      key={item.key}
                      onClick={() => handleSubscriptionChange(item.key)}
                    >
                      {item.label}
                    </Dropdown.Item>
                  </Tooltip>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
      </Row>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Row>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("ClientPaymentTab.monthlyPrice")}
              </p>
              <p
                className="ms-1 fs-6 font-normal"
                disabled={
                  selectClientStatus === "Inactive" ||
                  selectClientStatus === "InActive"
                }
              >
                {(paymentOverview?.currency || "N/A") +
                  " " +
                  (paymentOverview?.monthlyAmount || "N/A")}{" "}
                <i
                  className={`bi bi-pencil-fill ${
                    selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive"
                      ? "text-muted pointer-events-none"
                      : "cursor-pointer"
                  }`}
                  onClick={() => {
                    if (
                      selectClientStatus !== "Inactive" ||
                      selectClientStatus !== "InActive"
                    ) {
                      openDialogFromPencil();
                    }
                  }}
                ></i>
              </p>
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("Common.subscription") + " " + trans("Common.duration")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {paymentOverview?.subscriptionDuration}
              </p>
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("Common.billingCycle")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {billingCycles?.find(
                  (cycle) => cycle.value === paymentOverview?.billingCycle
                )?.label || "N/A"}
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={3} sm={3} md={3} lg={3}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {`${trans("Common.payments")}`}
              </p>
              <div className="payment-bars">
                {paymentMonthStatus?.map((payment, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <>
                        {payment.date + "  " + payment.month}
                        <br />
                        Payment {payment.paymentStatus}
                        <br />
                        Amount {payment.amount}
                      </>
                    }
                  >
                    <div
                      className="payment-bar"
                      style={{
                        backgroundColor: getPaymentBarColor(
                          payment.paymentStatus
                        ),
                      }}
                    />
                  </Tooltip>
                ))}
              </div>
            </Col>
          </Row>
        </>
      )}
      <Row className="align-items-center mt-3">
        <h6>{trans("Common.payment") + " " + trans("Common.history")}</h6>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={paymentDataLoading}
        pagination={{
          className: "custom-pagination-payment",
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          position: ["bottomRight"],
          showTotal: () => <Text>{getShowingText()}</Text>,
        }}
        onChange={handlePageChange}
        rowKey={(record) => record.id}
        locale={{
          emptyText: !paymentDataLoading && "No payment details data available",
        }}
        components={{
          table: (props) => (
            <table
              {...props}
              style={{
                ...props.style,
                borderCollapse: "separate",
                borderSpacing: "0 10px",
              }}
            />
          ),
          header: {
            cell: (props) => {
              return (
                <th
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "#FCFBFFCC",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    fontFamily: "Proxima-Nova-Regular",
                    padding: "12px 16px",
                    // Border radius for header cells
                    borderRadius:
                      props.children === columns[0].title
                        ? "12px 0 0 12px"
                        : props.children === columns[columns.length - 1].title
                        ? "0 12px 12px 0"
                        : "0",
                  }}
                />
              );
            },
            row: (props) => (
              <tr
                {...props}
                style={{
                  ...props.style,
                  background: "transparent",
                  fontFamily: "Proxima-Nova-Regular",
                }}
              />
            ),
          },
          body: {
            cell: (props) => (
              <td
                {...props}
                style={{
                  ...props.style,
                  background: "transparent",
                  borderColor: "transparent",
                  borderStyle: "solid",
                  borderWidth: "8px 0px",
                  padding: "12px 16px",
                  fontFamily: "Proxima-Nova-Regular",
                  // Border radius for body cells
                  borderRadius:
                    props.index % columns.length === 0
                      ? "12px 0 0 12px"
                      : props.index % columns.length === columns.length - 1
                      ? "0 12px 12px 0"
                      : "0",
                }}
              />
            ),
            row: (props) => (
              <tr
                {...props}
                style={{
                  ...props.style,
                  background: "transparent",
                  fontFamily: "Proxima-Nova-Regular",
                  margin: "10px 0",
                }}
              />
            ),
          },
        }}
      />

      {isDialogOpen && !isPauseSubscription && !isEndSubscription && (
        <Modal
          show={isDialogOpen}
          onHide={closeDialog}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title className="fs-5">
              {isEditFromTable
                ? trans("ClientPaymentTab.updateMonthlyPayment")
                : trans("ClientPaymentTab.UpdateRecurringPayment")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-1">
              <Col>
                <Form.Group controlId="formCurrencyValue">
                  <FormLabel label={trans("Common.amount")} required />
                  <div className="position-relative">
                    <FormControl
                      type="number"
                      name="currencyValue"
                      value={currencyValue}
                      onChange={onCurrencyValueChange}
                      isInvalid={!!errors.currencyValue}
                      feedback={errors.currencyValue}
                      height={"40"}
                      className="rounded-3 pe-5"
                    />
                    <p
                      className="position-absolute mb-0 lb-text"
                      style={{
                        top: "50%",
                        right: "10px",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {paymentOverview?.currency}
                    </p>
                  </div>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group controlId="formDueDate">
                  <FormLabel
                    label={
                      isEditFromTable
                        ? trans("ClientPaymentTab.dueDate")
                        : trans("ClientPaymentTab.fromDate")
                    }
                    required
                  />
                  <div className="d-flex flex-column">
                    <div
                      className="position-relative date-picker"
                      style={{ width: "fit-content" }}
                    >
                      <DatePicker
                        selected={isEditFromTable ? dueDate : updateFromDate}
                        onChange={(date) =>
                          isEditFromTable
                            ? setDueDate(date)
                            : setUpdateFromDate(date)
                        }
                        dateFormat="MMM d, yyyy"
                        placeholderText="Select Date"
                        className={`custom-datepicker w-100 ${
                          errors.dueDate ? "is-invalid" : ""
                        }`}
                        minDate={
                          isEditFromTable
                            ? new Date(
                                new Date().setDate(new Date().getDate() + 1)
                              )
                            : new Date(
                                new Date().setMonth(
                                  new Date().getMonth() + 1,
                                  1
                                )
                              )
                        }
                        maxDate={new Date(subscriptionEndDate)}
                      />
                      <i className="bi bi-calendar3 calendar-icon"></i>
                    </div>
                    {isEditFromTable ? (
                      errors.dueDate && (
                        <div className="invalid-feedback d-block">
                          {errors.dueDate}
                        </div>
                      )
                    ) : (
                      <div className="invalid-feedback d-block">
                        {errors.updateFromDate}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-primary"
              onClick={closeDialog}
              size="sm"
              className="cancel-button w-25 p-1"
            >
              Cancel
            </Button>

            <Button
              size="sm"
              className="saveButton w-25 ml-2"
              onClick={handleConfirm}
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal for Pause Subscription and End Subscription */}
      <Modal
        show={isDialogOpen && (isPauseSubscription || isEndSubscription)}
        onHide={closeDialog}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            {isPauseSubscription
              ? trans("ClientPaymentTab.pauseSubscription")
              : trans("ClientPaymentTab.endSubscription")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Conditional rendering for Pause Subscription */}
          {isPauseSubscription ? (
            <Form.Group controlId="formResumeDate">
              <div className="d-flex flex-column">
                <FormLabel
                  label={trans("ClientPaymentTab.resumeDate")}
                  required
                />
                <div className="d-flex flex-column">
                  <div
                    className="position-relative date-picker"
                    style={{ width: "fit-content" }}
                  >
                    <DatePicker
                      selected={resumeSubscriptionDate}
                      onChange={(date) => setResumeSubscriptionDate(date)}
                      dateFormat="MMM d, yyyy"
                      placeholderText="Select Date"
                      className={`custom-datepicker w-100 ${
                        errors.resumeSubscriptionDate ? "is-invalid" : ""
                      }`}
                      minDate={
                        new Date(new Date().setDate(new Date().getDate() + 2))
                      }
                    />
                    <i className="bi bi-calendar3 calendar-icon"></i>
                  </div>
                  {errors.resumeSubscriptionDate && (
                    <div className="invalid-feedback d-block">
                      {errors.resumeSubscriptionDate}
                    </div>
                  )}
                </div>
              </div>
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Check
                type="radio"
                label="End Subscription Immediately"
                name="endSubscriptionToday"
                checked={selectedOption === "endSubscriptionToday"}
                onChange={() => setSelectedOption("endSubscriptionToday")}
              />
              <Form.Check
                type="radio"
                label="End After Current Payment Period"
                name="endSubscriptionAfterCurrentPaymentPeriod"
                checked={
                  selectedOption === "endSubscriptionAfterCurrentPaymentPeriod"
                }
                onChange={() =>
                  setSelectedOption("endSubscriptionAfterCurrentPaymentPeriod")
                }
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={closeDialog}
            size="sm"
            className="back_button w-25 p-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="saveButton w-25 ml-2"
            onClick={handleConfirm}
          >
            {isPauseSubscription ? "Pause" : "End"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/*Confirmation modal for Pause Subscription and End Subscription */}
      <Modal
        show={isSubscriptionConfirmation}
        onHide={() => setIsSubscriptionConfirmation(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            {trans("Common.confirmAction")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormLabel label={subscriptionConfirmationMessage} className="fs-5" />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setIsSubscriptionConfirmation(false)}
            size="sm"
            className="back_button w-25 p-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="saveButton w-25 ml-2"
            onClick={handleSubscriptionConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientPaymentDetailsTab;
